// Analyses ONLY the already-authorized local voice stream supplied by the page.
// No microphone request, peer creation, recording, or sample transmission.
export const VOICE_ACTIVITY = Object.freeze({ on: 0.018, off: 0.010, attackFrames: 2,
  releaseMs: 300, sampleMs: 100, reportMs: 200, heartbeatMs: 1000 });

export function createVoiceActivityPublisher({ stream, muted = false, publish,
  AudioContextClass = typeof window !== 'undefined' ? window.AudioContext || window.webkitAudioContext : null,
  now = Date.now, every = setInterval, cancel = clearInterval }) {
  let context, source, analyser, timer, stopped = false;
  let speaking = false, attack = 0, lastLoudAt = 0;
  let lastReportAt = -Infinity, lastReport;
  const report = (value, immediate = false) => {
    if (stopped) return;
    const at = now();
    if (!immediate && at - lastReportAt < VOICE_ACTIVITY.reportMs) return;
    if (value === lastReport && at - lastReportAt < VOICE_ACTIVITY.heartbeatMs) return;
    lastReportAt = at;
    lastReport = value;
    publish(value); // Only boolean/null leaves this module; samples remain local.
  };
  const disconnect = node => { try { node?.disconnect(); } catch {} };
  const close = () => { try { context?.close()?.catch?.(() => {}); } catch {} };
  if (!stream || muted) { report(false, true); return () => { stopped = true; }; }
  if (!AudioContextClass) { report(null, true); return () => { stopped = true; }; }
  try {
    context = new AudioContextClass();
    analyser = context.createAnalyser();
    analyser.fftSize = 512;
    source = context.createMediaStreamSource(stream);
    source.connect(analyser); // Never connect to an output or modify shared tracks.
    if (context.state === 'suspended') {
      try { context.resume()?.catch?.(() => {}); } catch {}
    }
  } catch {
    disconnect(source); disconnect(analyser); close();
    report(null, true);
    return () => { stopped = true; };
  }
  const samples = new Float32Array(analyser.fftSize);
  const reset = value => {
    speaking = false; attack = 0; lastLoudAt = 0;
    report(value, true);
  };
  const trackChanged = () => {
    const tracks = stream.getAudioTracks();
    if (!tracks.some(t => t.enabled !== false && !t.muted && t.readyState !== 'ended')) reset(false);
  };
  const contextChanged = () => { if (context.state !== 'running') reset(null); };
  const tracks = stream.getAudioTracks();
  tracks.forEach(track => { track.addEventListener?.('mute', trackChanged); track.addEventListener?.('ended', trackChanged); });
  context.addEventListener?.('statechange', contextChanged);
  report(null, true); // Unavailable until a running analyser actually reads audio.
  timer = every(() => {
    if (stopped) return;
    const usable = stream.getAudioTracks().some(t => t.enabled !== false && !t.muted && t.readyState !== 'ended');
    if (!usable) { speaking = false; attack = 0; report(false); return; }
    if (context.state !== 'running') { speaking = false; attack = 0; report(null); return; }
    try {
      analyser.getFloatTimeDomainData(samples);
      let sum = 0;
      for (let index = 0; index < samples.length; index += 1) sum += samples[index] * samples[index];
      const rms = Math.sqrt(sum / samples.length);
      const at = now();
      if (rms >= VOICE_ACTIVITY.on) {
        attack += 1;
        if (attack >= VOICE_ACTIVITY.attackFrames) speaking = true;
        lastLoudAt = at;
      } else {
        attack = 0;
        if (speaking && rms >= VOICE_ACTIVITY.off) lastLoudAt = at;
        if (speaking && at - lastLoudAt >= VOICE_ACTIVITY.releaseMs) speaking = false;
      }
      report(speaking);
    } catch { speaking = false; attack = 0; report(null); }
  }, VOICE_ACTIVITY.sampleMs);
  return () => {
    stopped = true;
    cancel(timer);
    tracks.forEach(track => { track.removeEventListener?.('mute', trackChanged); track.removeEventListener?.('ended', trackChanged); });
    context.removeEventListener?.('statechange', contextChanged);
    disconnect(source); disconnect(analyser); close(); samples.fill(0);
    // Stream ownership remains with LiveStream: do not stop its tracks.
  };
}
